/**
 * Path of the general Smart Apply file-upload endpoint, appended to `smartApplyBaseUrl`. Matches
 * mosaic's BFF client (`basePath: '/api/v1'` → `POST /api/v1/files`), which returns
 * `{ name, contentType, fileToken }`.
 */
export const FILE_UPLOAD_PATH = '/api/v1/files';

/**
 * `.indeed.com`-scoped CSRF cookie the server mints (`sharedCsrfCookieMiddleware`) and this client
 * echoes as the `X-XSRF-TOKEN` header on the cross-origin BFF upload. NOT the host-scoped
 * `INDEED_CSRF_TOKEN`, which the browser never sends to the BFF's host, so it could never match the
 * BFF's double-submit check. The server middleware and the JSF host re-declare this same literal —
 * it's an external platform contract (smart-apply-bff's `EitherIndeedCsrfTokenRepository`), not a
 * value we own, and it also has to cross a repo boundary into jobseeker-frontend.
 */
export const SHARED_CSRF_COOKIE_NAME = 'SHARED_INDEED_CSRF_TOKEN';

/**
 * Builds the absolute upload URL, or '' when no base is configured — in which case `uploadFile`
 * throws `'File upload is not configured'` rather than silently no-opping.
 */
export const buildFileUploadUrl = (smartApplyBaseUrl: string | undefined): string =>
    smartApplyBaseUrl ? `${smartApplyBaseUrl}${FILE_UPLOAD_PATH}` : '';

export interface FileUploadContext {
    /** Absolute upload endpoint URL (see `buildFileUploadUrl`). */
    uploadUrl: string;
    /** IA session id, forwarded as the BFF's required `IA-Application-ID` header when available. */
    iaUid?: string;
    /**
     * The job's Indeed Apply API token (`smartApplyContext.jobContext.employer.apiToken`), sent as
     * the BFF's required `IA-API-KEY` header. `smart-apply-bff` protects `/api/v1/**` with
     * `ApiKeyAuthFilter` + `anyRequest().authenticated()`, so the POST is rejected with 403 without a
     * valid key. This mirrors the in-apply flow: smart-apply-webapp feeds the same per-job apiToken
     * to `commonMetadata.apiToken`, which mosaic's BFF client sends as `IA-API-KEY`.
     */
    apiKey?: string;
    /**
     * BFF `IA-Upload-Category` (tracking/logging), e.g. `'screener_question'`. Optional; one of
     * `resume | supporting_document | screener_question | smart_apply_frontend_state`.
     */
    uploadCategory?: string;
}

/** Reads a browser cookie value by name (client-only; returns undefined during SSR). */
const readCookie = (name: string): string | undefined => {
    if (typeof document === 'undefined') return undefined;
    return document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(`${name}=`))
        ?.slice(name.length + 1);
};

/** A file that has been uploaded and assigned a token by the file service. */
export interface UploadedFile {
    /** Display name (original filename). */
    name: string;
    /** Opaque token the persist URI is built from. */
    fileToken: string;
    contentType?: string;
}

/** The minimal upload response shape this app needs. */
interface FileUploadResponse {
    fileToken: string;
    name?: string;
    contentType?: string;
}

/**
 * Uploads a file to the general Smart Apply file endpoint and resolves the uploaded file (display
 * name + `fileToken`). A plain async function (no React state/identity concerns) called imperatively
 * from an event handler. Adapted to this app's cross-origin cookie-auth `fetch` convention (see
 * `pages/auto-apply/review/downloadPreviewHtml.ts`). Throws on a non-2xx (or tokenless 2xx) response
 * so the caller can surface the error and drop any optimistic placeholder.
 */
export const uploadFile = async (context: FileUploadContext, file: File): Promise<UploadedFile> => {
    const { uploadUrl, iaUid, apiKey, uploadCategory } = context;
    if (!uploadUrl) throw new Error('File upload is not configured');

    const body = new FormData();
    body.append('file', file);

    const headers: Record<string, string> = {};
    // The BFF authenticates the calling app via IA-API-KEY (AuthConfig / ApiKeyAuthFilter); without
    // it the credentialed POST is rejected with 403 before reaching FilesController. Mosaic's BFF
    // client sends the same value as its `apiKey`.
    if (apiKey) headers['IA-API-KEY'] = apiKey;
    // The BFF's required IA session header (smart-apply-bff FilesController / api-specs
    // ApplicationID.yml). Not the OneGraph `indeed-root-tracking-key` context key the Apollo
    // mutations use.
    if (iaUid) headers['IA-Application-ID'] = iaUid;
    if (uploadCategory) headers['IA-Upload-Category'] = uploadCategory;
    // Spring CSRF double-submit: browsers don't auto-attach the XSRF header on a bare fetch (mosaic's
    // axios client does via `xsrfCookieName`), so mirror the cookie ourselves. This is a cross-origin
    // POST (js-pro-preference → smart-apply-bff), so the token must be the `.indeed.com`-scoped
    // SHARED_INDEED_CSRF_TOKEN the server mints (sharedCsrfCookieMiddleware); the host-scoped
    // INDEED_CSRF_TOKEN isn't sent to the BFF's host, so it could never match. Mirrors mosaic's
    // cross-origin `xsrfCookieName: 'SHARED_INDEED_CSRF_TOKEN'`.
    const xsrfToken = readCookie(SHARED_CSRF_COOKIE_NAME);
    if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken;

    const response = await fetch(uploadUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
    });
    if (!response.ok) {
        throw new Error(`File upload failed: ${response.status}`);
    }

    const data = (await response.json()) as FileUploadResponse;
    // A 2xx without a token can't be persisted (buildScreenerAnswerInput would drop it and the
    // jobseeker would silently submit no file), so treat it as a failure — the caller then drops the
    // optimistic placeholder and surfaces the error.
    if (!data.fileToken) {
        throw new Error('File upload response missing fileToken');
    }
    return {
        name: data.name || file.name,
        fileToken: data.fileToken,
        contentType: data.contentType,
    };
};
